import { FC, useMemo } from 'react';
import { TypeRedesignHomepageFields } from '@/core/generated-types';
import { HeaderText } from '@/components';
import { HeaderSize } from '@/components/ui/HeaderText';
import Bullet from './Bullet';
import { Entry } from 'contentful';
import Image from 'next/image';
import { getSafeImageUrl } from '../utils';
import { useIsDesktopWithResize } from '@/hooks/useIsDesktopWithResize';

export type WhatIsItProps = {
  images: TypeRedesignHomepageFields['whatIsItImages'];
  translations?: Entry<Record<string, any>>;
};

const WhatIsIt: FC<WhatIsItProps> = ({ images = [], translations }) => {
  const isDesktop = useIsDesktopWithResize();

  const imagesByBreakpoint = isDesktop
    ? images.filter((image) => image.fields.title.includes('Desktop Static'))
    : images.filter((image) => image.fields.title.includes('Mobile Static'));
  const translationsObject = useMemo(
    () =>
      (
        translations?.fields.keyValues as {
          id: string;
          key: string;
          value: string;
        }[]
      ).reduce(
        (prev, current) => {
          return { ...prev, [current.key]: current.value };
        },
        {} as Record<string, string>,
      ),
    [translations],
  );

  return (
    <div className="flex flex-col">
      <div className="lg:hidden flex">
        <div className="flex flex-col w-full">
          {imagesByBreakpoint.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-center w-full h-full"
            >
              <img
                className="object-fit object-center w-full h-full"
                src={item.fields.file.url}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="lg:hidden w-full bg-white flex flex-col px-6 py-8">
        <HeaderText
          omitSizeString
          size={HeaderSize.H2}
          text={translationsObject.header}
          classes="text-d-h2 lg:text-[42px] lg:leading-[50px] mb-6 xl:mb-[50px]"
        />
        <ul className="flex flex-col gap-4 xl:gap-6 md:mb-8 xl:mb-16">
          <Bullet>{translationsObject.bullet1}</Bullet>
          <Bullet>{translationsObject.bullet2}</Bullet>
          <Bullet>{translationsObject.bullet3}</Bullet>
        </ul>
      </div>
      <div className="hidden lg:flex max-w-[1220px] mx-auto w-full lg:px-6 lg:items-center">
        <div className="flex flex-col justify-center h-[50vw] w-[500px]">
          <div className="flex flex-col">
            <HeaderText
              omitSizeString
              size={HeaderSize.H2}
              text={translationsObject.header}
              classes="text-d-h2 lg:text-[42px] lg:leading-[50px] mb-6 xl:mb-[50px]"
            />
            <ul className="flex flex-col gap-4 xl:gap-6 md:mb-8 xl:mb-16">
              <Bullet>{translationsObject.bullet1}</Bullet>
              <Bullet>{translationsObject.bullet2}</Bullet>
              <Bullet>{translationsObject.bullet3}</Bullet>
            </ul>
            <div className="hidden flex justify-start md:flex">
              <div className="flex justify-center items-center p-4 border-2 border-lime-green rounded-md max-w-[450px]">
                <img
                  src="https://images.ctfassets.net/0sea1vycfyqy/6c7MzhtuLBmb6838cGjvut/ee23f67edd0e5ab4e3448de9a5c781da/Truck.png"
                  alt="truck"
                  className="mr-8 w-[60px] h-auto"
                />
                <p className="font-bold text-d-sm">
                  {translationsObject.shippingNote}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white w-[500px]"></div>
      </div>
      <div className="hidden lg:flex flex mt-[-50vw]">
        <div className="md:w-1/2"></div>
        <div className="flex flex-col w-full md:h-full md:w-1/2">
          {imagesByBreakpoint.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-center w-full h-full"
            >
              <Image
                alt="Toddler playing with ball drop"
                width={item.fields.file.details.image?.width}
                height={item.fields.file.details.image?.height}
                src={getSafeImageUrl(item)}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="lg:hidden bg-white flex justify-center items-center mx-8 p-4 border-2 border-lime-green rounded-md mb-8">
        <img
          src="https://images.ctfassets.net/0sea1vycfyqy/6c7MzhtuLBmb6838cGjvut/ee23f67edd0e5ab4e3448de9a5c781da/Truck.png"
          alt="truck"
          className="mr-8 w-[60px] h-auto"
        />
        <p className="font-bold text-d-sm">{translationsObject.shippingNote}</p>
      </div>
    </div>
  );
};

export default WhatIsIt;
